

import { useContext, useState } from "react";
import { useTranslation } from "@fmc-digital/i18n";
import { RegisterContext } from "../RegisterContext";
import { MFACodeContainerV2 } from "../../../components/organisms/MFACodeContainer/MFACodeContainerV2";
import { triggerOtp } from "@fmc-digital/auth-api-generated/src/api/fmc/unauth/customer/triggerOtp";
import { validateOtp } from "@fmc-digital/auth-api-generated/src/api/fmc/unauth/customer/validateOtp";
import type { RegisterTriggerOTPRequest } from "@fmc-digital/auth-api-generated/types/fmc/unauth";
import { loadConfig } from "@/config/auth.config";
import {
  extractApiErrorCode,
  getRegisterErrorMessageFromCode,
  handleRegisterOtpValidationError,
  type RegisterOtpValidationResult,
} from "@/utils/registerErrorMessage";
import { trackRegistrationEvent, RegistrationEvent } from "@/utils/analytics.utils";

interface OtpStepProps {
  setStatusMessage: (msg: string | null) => void;
  enableResendOnLoad?: boolean;
  onConsumeEnableResendOnLoad?: () => void;
}

export function OtpStep({
  setStatusMessage,
  enableResendOnLoad,
  onConsumeEnableResendOnLoad,
}: OtpStepProps) {
  const { t, i18n } = useTranslation();
  const { setOtp, email, setStep, setEmailSubstep, otpId, otpFactorId, setOtpId, setOtpFactorId, goBack } = useContext(RegisterContext);
  const [otpError, setOtpError] = useState<string | null>(null);
  const translate = (key: string) => t(key as never) as string;

  // Minimal factorState for registration (email only)
  const factorState = {
    factorType: "email" as const,
    profile: { email },
  };

  const trackEmailOtpValidationFailed = (
    result: RegisterOtpValidationResult | undefined,
    errorCode: string | null,
  ) => {
    if (
      result?.status === "CLEAR_CODE" ||
      result?.status === "ALREADY_VALIDATED" ||
      result?.status === "BANNER_ONLY"
    ) {
      return;
    }
    try {
      trackRegistrationEvent(RegistrationEvent.EMAIL_OTP_VALIDATION_FAILED, {
        ...(errorCode ? { errorCode } : {}),
      });
    } catch {
      // Analytics must not block flow
    }
  };

  const handleValidate = async (code: string) => {
    setStatusMessage(null);
    setOtpError(null);
    // Null/empty validation for otpId and otpFactorId
    if (!otpId || !otpFactorId) {
      setOtpError("Something went wrong.");
      return null;
    }
    try {
      trackRegistrationEvent(RegistrationEvent.EMAIL_OTP_SUBMITTED);
    } catch {
      // Analytics must not block validation
    }
    try {
      return await validateOtp({
        factorValue: code,
        id: otpId,
        factorId: otpFactorId,
      });
    } catch (err) {
      const errorCode = extractApiErrorCode(err);
      const message = getRegisterErrorMessageFromCode(
        err,
        translate,
        i18n.exists.bind(i18n),
      );

      const result = handleRegisterOtpValidationError(
        errorCode,
        message,
        setStatusMessage,
        setOtpError,
      );
      trackEmailOtpValidationFailed(result, errorCode);
      return result;
    }
  };

  const handleVerify = (verifiedCode: string) => {
    setStatusMessage(null);
    try {
      trackRegistrationEvent(RegistrationEvent.EMAIL_OTP_VALIDATION_SUCCESS);
    } catch {
      // Analytics must not block navigation
    }
    setOtp(verifiedCode);
    setStep("personalInfo");
    setEmailSubstep("input");
  };

  // Clear OTP error when user edits the code
  const handleInputEdit = () => {
    if (otpError) setOtpError(null);
    setStatusMessage(null);
  };

  const handleCancel = () => {
    setOtpError(null);
    setStatusMessage(null);
    goBack();
  };

  const handleResend = async () => {
    setStatusMessage(null);
    let prevBaseUrl: string | undefined = undefined;

    try {
      const config = await loadConfig();
      const baseUrlV2 = config.api.baseUrlV2 || config.api.baseUrl;

      if (typeof window !== "undefined" && window.__authShellConfig) {
        prevBaseUrl = window.__authShellConfig.apiBaseUrl;
        window.__authShellConfig.apiBaseUrl = `${baseUrlV2}/unauth`;
      }

      const payload: RegisterTriggerOTPRequest = {
        factor: {
          factorType: "EMAIL",
          email,
        },
      };

      const response = await triggerOtp(payload);

      if (response?.id) {
        setOtpId(response.id);
        setOtpFactorId(response.factorId || "");
      } else {
        throw new Error("register:otp_trigger_failed");
      }
    } catch (err) {
      const message = getRegisterErrorMessageFromCode(
        err,
        translate,
        i18n.exists.bind(i18n),
      );
      setStatusMessage(message);
      throw err;
    } finally {
      if (typeof window !== "undefined" && window.__authShellConfig && prevBaseUrl !== undefined) {
        window.__authShellConfig.apiBaseUrl = prevBaseUrl;
      }
    }
  };

  return (
    <MFACodeContainerV2
      factorState={factorState}
      onVerify={handleVerify}
      onCancel={handleCancel}
      onValidate={handleValidate}
      onResend={handleResend}
      inputError={otpError}
      onInputEdit={handleInputEdit}
      enableResendOnLoad={enableResendOnLoad}
      onConsumeEnableResendOnLoad={onConsumeEnableResendOnLoad}
    />
  );
}
